import React, { useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { CalendarIcon, Check, Loader2, Utensils } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIMES = [
  "12:00", "12:30", "13:00", "13:30", "14:00", "14:30",
  "17:00", "17:30", "18:00", "18:30", "19:00", "19:30",
  "20:00", "20:30", "21:00", "21:30",
];

const PARTY_SIZES = [1, 2, 3, 4, 5, 6, 7, 8, 10, 12];

const initial = {
  name: "",
  phone: "",
  email: "",
  time: "",
  party_size: "",
  message: "",
  newsletter: false,
};

export default function Reservation() {
  const [form, setForm] = useState(initial);
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      toast.error("Please fill in name, phone and email.");
      return;
    }
    if (!date || !form.time || !form.party_size) {
      toast.error("Please pick a date, time and party size.");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/reservations`, {
        name: form.name,
        phone: form.phone,
        email: form.email,
        date: format(date, "yyyy-MM-dd"),
        time: form.time,
        party_size: Number(form.party_size),
        message: form.message,
        newsletter: form.newsletter,
      });
      toast.success("Reservation request sent — we'll call to confirm!");
      setSubmitted(true);
      setForm(initial);
      setDate(null);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again or call us.";
      toast.error(typeof msg === "string" ? msg : "Could not submit reservation.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="reserve"
      data-testid="reservation-section"
      className="py-20 md:py-32"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <span className="chip">Book a table</span>
            <h2 className="mt-5 font-display font-black tracking-tighter text-4xl md:text-5xl">
              Save your <span className="italic font-light">seat</span> at the
              wok.
            </h2>
            <p className="mt-5 text-[16px] text-[#5C534D] leading-relaxed">
              We're a small room and fill up fast — especially Fri & Sat.
              Request below and our team will call back within a few hours to
              confirm.
            </p>
            <div className="mt-10 rounded-2xl bg-[#1A1A1A] text-white p-6">
              <div className="flex items-center gap-3">
                <Utensils className="h-5 w-5 text-[#F5A623]" />
                <div className="text-[11px] tracking-[0.3em] uppercase font-bold text-[#F5A623]">
                  Walk-ins welcome
                </div>
              </div>
              <p className="mt-3 text-sm text-white/80 leading-relaxed">
                Can't find a slot? Call us directly — we'll do our best to squeeze you in at the counter.
              </p>
            </div>
          </div>

          <div className="lg:col-span-8">
            <form
              data-testid="reservation-form"
              onSubmit={onSubmit}
              className="rounded-3xl bg-white border border-[#E8DED5] p-6 md:p-10 shadow-[0_20px_60px_-30px_rgba(26,26,26,0.25)]"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field id="res-name" label="Full name *">
                  <Input
                    id="res-name"
                    data-testid="res-name"
                    placeholder="Somchai Tan"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    required
                    className="h-12 rounded-xl"
                  />
                </Field>
                <Field id="res-phone" label="Phone *">
                  <Input
                    id="res-phone"
                    data-testid="res-phone"
                    type="tel"
                    placeholder="+44 ..."
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    required
                    className="h-12 rounded-xl"
                  />
                </Field>
                <Field id="res-email" label="Email *" className="md:col-span-2">
                  <Input
                    id="res-email"
                    data-testid="res-email"
                    type="email"
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    required
                    className="h-12 rounded-xl"
                  />
                </Field>

                <Field id="res-date" label="Date *">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        data-testid="res-date-trigger"
                        className="h-12 w-full justify-start rounded-xl font-normal"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {date ? format(date, "EEE, d MMM yyyy") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        disabled={(d) => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          if (d < today) return true;
                          if (d.getDay() === 1) return true; // Monday closed
                          return false;
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </Field>

                <Field id="res-time" label="Time *">
                  <Select
                    value={form.time}
                    onValueChange={(v) => update("time", v)}
                  >
                    <SelectTrigger
                      id="res-time"
                      data-testid="res-time"
                      className="h-12 rounded-xl"
                    >
                      <SelectValue placeholder="Select a time" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMES.map((t) => (
                        <SelectItem key={t} value={t} data-testid={`res-time-${t}`}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <Field id="res-party" label="Party size *">
                  <Select
                    value={form.party_size}
                    onValueChange={(v) => update("party_size", v)}
                  >
                    <SelectTrigger
                      id="res-party"
                      data-testid="res-party"
                      className="h-12 rounded-xl"
                    >
                      <SelectValue placeholder="How many guests?" />
                    </SelectTrigger>
                    <SelectContent>
                      {PARTY_SIZES.map((n) => (
                        <SelectItem key={n} value={String(n)} data-testid={`res-party-${n}`}>
                          {n} {n === 1 ? "guest" : "guests"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <Field
                  id="res-message"
                  label="Special requests (optional)"
                  className="md:col-span-2"
                >
                  <Textarea
                    id="res-message"
                    data-testid="res-message"
                    rows={4}
                    placeholder="Allergies, high-chair, birthday, anything we should know..."
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    className="rounded-xl"
                  />
                </Field>

                <div className="md:col-span-2 flex items-center gap-3">
                  <Checkbox
                    id="res-newsletter"
                    data-testid="res-newsletter"
                    checked={form.newsletter}
                    onCheckedChange={(v) => update("newsletter", !!v)}
                  />
                  <Label
                    htmlFor="res-newsletter"
                    className="text-sm text-[#5C534D] font-normal cursor-pointer"
                  >
                    Yes, I'd like occasional news about promotions & new dishes.
                  </Label>
                </div>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <Button
                  type="submit"
                  data-testid="res-submit"
                  disabled={loading}
                  className="rounded-full bg-[#E53935] hover:bg-[#C62828] text-white h-14 px-8 text-base font-semibold min-w-[200px]"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                      Sending...
                    </>
                  ) : submitted ? (
                    <>
                      <Check className="h-5 w-5 mr-2" />
                      Request sent
                    </>
                  ) : (
                    "Request reservation"
                  )}
                </Button>
                <p className="text-xs text-[#5C534D]">
                  By submitting you agree we may contact you about your booking.
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ id, label, children, className = "" }) {
  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      <Label
        htmlFor={id}
        className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#5C534D]"
      >
        {label}
      </Label>
      {children}
    </div>
  );
}
